import { useEffect, useMemo, useCallback } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  TPlanningTab,
  TPlanningFilters,
  TPlanningPatientMedEventsRequest,
  EPlanningColumns,
  TPlanningOrder,
  EPlanningOrderDirection,
  TSearchPlanningTab,
} from './planning.type'
import { ALL_PLANNING_TAB_ID } from './planning-all-tab.util'

const PLANNING_RELATIVE_DAY = 'PLANNING_RELATIVE_DAY'

type TStoredPlanningRelativeDay = {
  tabId: number
  relativeDaysFromToday: number
}

const setStoredPlanningRelativeDay = (tabId: number, relativeDaysFromToday: number) => {
  sessionStorage?.setItem(PLANNING_RELATIVE_DAY, JSON.stringify({ tabId, relativeDaysFromToday }))
}

const getStoredPlanningRelativeDay = (): TStoredPlanningRelativeDay | undefined => {
  const item = sessionStorage?.getItem(PLANNING_RELATIVE_DAY)
  return item && JSON.parse(item)
}

const clearStoredPlanningRelativeDay = () => {
  sessionStorage?.removeItem(PLANNING_RELATIVE_DAY)
}

// Returns the persisted relativeDaysFromToday, or '0' when nothing has been stored yet
const getPersistedRelativeDaysFromToday = (): string => {
  const stored = getStoredPlanningRelativeDay()
  return stored ? stored.relativeDaysFromToday.toString() : '0'
}

interface UsePlanningStateProps {
  sortedTabs: TPlanningTab[]
  allPlanningTab: TSearchPlanningTab
}

interface UsePlanningStateReturn {
  currentTab: TPlanningTab | TSearchPlanningTab
  currentFilters: TPlanningFilters
  currentPage: number
  currentOrder: TPlanningOrder
  moveToTab: (tab: TPlanningTab | TSearchPlanningTab) => void
  updatePlanningFilters: (filters: Partial<TPlanningFilters>) => void
  updateOrder: (orderBy: TPlanningOrder) => void
  updatePageNumber: (newPageNumber: number) => void
  resetFilters: () => void
  getApiParams: () => TPlanningPatientMedEventsRequest
}

export function usePlanningState({
  sortedTabs,
  allPlanningTab,
}: UsePlanningStateProps): UsePlanningStateReturn {
  const location = useLocation()
  const pathname = location.pathname
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const tabIdFromUrl = searchParams.get('tabId')
  const relativeDaysFromUrl = searchParams.get('relativeDaysFromToday')
  const medEventIdsFromUrl = searchParams.get('medEventIds')
  const followupIdsFromUrl = searchParams.get('followupIds')
  const medicalStaffMemberIdsFromUrl = searchParams.get('medicalStaffMemberIds')
  const locationIdsFromUrl = searchParams.get('locationIds')
  const orderByFromUrl = searchParams.get('orderBy') || ''
  const orderDirectionFromUrl = searchParams.get('orderDirection') || ''
  const pageFromUrl = searchParams.get('page')

  // getters
  const currentTab = useMemo(() => {
    const firstTab = sortedTabs[0] as TPlanningTab
    if (!firstTab) {
      return allPlanningTab
    }
    if (tabIdFromUrl === null) {
      return firstTab
    }
    if (Number.parseInt(tabIdFromUrl) === ALL_PLANNING_TAB_ID) {
      return allPlanningTab
    }
    return sortedTabs.find((tab) => tab.id === Number.parseInt(tabIdFromUrl)) || firstTab
  }, [tabIdFromUrl, sortedTabs, allPlanningTab])

  const currentFilters = useMemo<TPlanningFilters>(() => {
    let newFilters: TPlanningFilters = { relativeDaysFromToday: 0 }
    if (medEventIdsFromUrl) {
      newFilters = {
        ...newFilters,
        medEventIds: medEventIdsFromUrl.split(',').map(Number).filter(Boolean),
      }
    }
    if (followupIdsFromUrl) {
      newFilters = {
        ...newFilters,
        followupIds: followupIdsFromUrl.split(',').map(Number).filter(Boolean),
      }
    }
    if (medicalStaffMemberIdsFromUrl) {
      newFilters = {
        ...newFilters,
        medicalStaffMemberIds: medicalStaffMemberIdsFromUrl
          .split(',')
          .map((medicalStaffMemberId) => Number.parseInt(medicalStaffMemberId) || null)
          .filter(
            (medicalStaffMemberId: number | null) =>
              medicalStaffMemberId === null || !isNaN(medicalStaffMemberId),
          ),
      }
    }
    if (locationIdsFromUrl) {
      newFilters = {
        ...newFilters,
        locationIds: locationIdsFromUrl.split(',').map(Number).filter(Boolean),
      }
    }
    if (relativeDaysFromUrl) {
      newFilters = {
        ...newFilters,
        relativeDaysFromToday: Number.parseInt(relativeDaysFromUrl),
      }
    }
    return newFilters
  }, [
    medEventIdsFromUrl,
    followupIdsFromUrl,
    medicalStaffMemberIdsFromUrl,
    locationIdsFromUrl,
    relativeDaysFromUrl,
  ])

  const currentOrder: TPlanningOrder = useMemo(() => {
    return {
      orderBy:
        orderByFromUrl in EPlanningColumns
          ? (orderByFromUrl as EPlanningColumns)
          : EPlanningColumns.MED_EVENT,
      orderDirection:
        orderDirectionFromUrl in EPlanningOrderDirection
          ? (orderDirectionFromUrl as EPlanningOrderDirection)
          : EPlanningOrderDirection.ASC,
    }
  }, [orderByFromUrl, orderDirectionFromUrl])

  const currentPage = useMemo(() => {
    if (!pageFromUrl) return 1
    const page = Number.parseInt(pageFromUrl)
    return page >= 1 ? page : 1
  }, [pageFromUrl])

  // setters
  const moveToTab = useCallback(
    (tab: TPlanningTab | TSearchPlanningTab) => {
      const newSearchParams = new URLSearchParams(searchParams.toString())
      newSearchParams.set('tabId', tab.id.toString())
      newSearchParams.set('page', '1')
      newSearchParams.set('orderBy', EPlanningColumns.MED_EVENT)
      newSearchParams.set('orderDirection', EPlanningOrderDirection.ASC)
      newSearchParams.set('medEventIds', (tab.medEventIdsFilter || []).join(','))
      newSearchParams.set('followupIds', (tab.followupIdsFilter || []).join(','))
      newSearchParams.set('medicalStaffMemberIds', (tab.medicalStaffMemberIdsFilter || []).join(','))
      newSearchParams.set('locationIds', (tab.locationIdsFilter || []).join(','))
      newSearchParams.set('relativeDaysFromToday', getPersistedRelativeDaysFromToday())
      return navigate(`${pathname}?${newSearchParams.toString()}`)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams.toString(), sortedTabs.length],
  )

  const updatePlanningFilters = useCallback(
    (newFilters: Partial<TPlanningFilters>) => {
      const newSearchParams = new URLSearchParams(searchParams.toString())
      if (newFilters.medEventIds !== undefined) {
        newFilters.medEventIds.length > 0
          ? newSearchParams.set('medEventIds', newFilters.medEventIds.join(','))
          : newSearchParams.delete('medEventIds')
      }
      if (newFilters.medicalStaffMemberIds !== undefined) {
        newFilters.medicalStaffMemberIds.length > 0
          ? newSearchParams.set(
              'medicalStaffMemberIds',
              newFilters.medicalStaffMemberIds.map(String).join(','), // map(String) to keep null value
            )
          : newSearchParams.delete('medicalStaffMemberIds')
      }
      if (newFilters.locationIds !== undefined) {
        newFilters.locationIds.length > 0
          ? newSearchParams.set('locationIds', newFilters.locationIds.join(','))
          : newSearchParams.delete('locationIds')
      }
      if (newFilters.relativeDaysFromToday !== undefined) {
        newSearchParams.set('relativeDaysFromToday', newFilters.relativeDaysFromToday.toString())
        setStoredPlanningRelativeDay(currentTab.id, newFilters.relativeDaysFromToday)
      }
      navigate(`${pathname}?${newSearchParams.toString()}`, { replace: true })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams.toString(), sortedTabs, currentTab.id],
  )

  const resetFilters = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams.toString())
    if (currentTab.id === ALL_PLANNING_TAB_ID) {
      newSearchParams.set('relativeDaysFromToday', '0')
      newSearchParams.delete('medEventIds')
      newSearchParams.delete('medicalStaffMemberIds')
      newSearchParams.delete('locationIds')
    } else {
      newSearchParams.set('relativeDaysFromToday', '0')
      newSearchParams.set('medEventIds', (currentTab.medEventIdsFilter || []).join(','))
      newSearchParams.set('medicalStaffMemberIds', (currentTab.medicalStaffMemberIdsFilter || []).join(','))
      newSearchParams.set('locationIds', (currentTab.locationIdsFilter || []).join(','))
    }
    clearStoredPlanningRelativeDay()
    navigate(`${pathname}?${newSearchParams.toString()}`, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()])

  const updateOrder = useCallback(
    (newOrder: { orderBy: EPlanningColumns; orderDirection: 'ASC' | 'DESC' }) => {
      const newSearchParams = new URLSearchParams(searchParams.toString())
      if (newOrder.orderBy !== undefined) {
        newSearchParams.set('orderBy', newOrder.orderBy)
      }
      if (newOrder.orderDirection !== undefined) {
        newSearchParams.set('orderDirection', newOrder.orderDirection)
      }
      navigate(`${pathname}?${newSearchParams.toString()}`, { replace: true })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams.toString()],
  )

  const updatePageNumber = useCallback(
    (newPageNumber: number) => {
      const newSearchParams = new URLSearchParams(searchParams.toString())
      const newPage = newPageNumber <= 1 ? 1 : newPageNumber
      if (newPage !== undefined) {
        newSearchParams.set('page', newPage.toString())
      }
      navigate(`${pathname}?${newSearchParams.toString()}`, { replace: true })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams.toString()],
  )

  const getApiParams = useCallback((): TPlanningPatientMedEventsRequest => {
    return {
      relativeDaysFromToday: currentFilters.relativeDaysFromToday,
      medEventIds: currentFilters.medEventIds,
      followupIds: currentTab.followupIdsFilter,
      medicalStaffMemberIds: currentFilters.medicalStaffMemberIds,
      locationIds: currentFilters.locationIds,
      columns: currentTab.columns, // should not be changed by user
      orderBy: currentOrder.orderBy,
      orderDirection: currentOrder.orderDirection,
      page: currentPage,
    }
  }, [currentPage, currentOrder.orderBy, currentOrder.orderDirection, currentFilters, currentTab])

  useEffect(() => {
    const hasTabId = searchParams.get('tabId')
    const hasPage = searchParams.get('page')
    const isPageValid = hasPage ? Number.parseInt(hasPage) >= 1 : false

    if (!hasTabId || !isPageValid) {
      const newSearchParams = new URLSearchParams(searchParams.toString())
      const firstTab = sortedTabs[0]
      if (!hasTabId && firstTab) {
        newSearchParams.set('tabId', firstTab.id.toString())
        newSearchParams.set('relativeDaysFromToday', getPersistedRelativeDaysFromToday())
        newSearchParams.set('medEventIds', (firstTab.medEventIdsFilter || []).join(','))
        newSearchParams.set('medicalStaffMemberIds', (firstTab.medicalStaffMemberIdsFilter || []).join(','))
        newSearchParams.set('locationIds', (firstTab.locationIdsFilter || []).join(','))
        newSearchParams.set('followupIds', (firstTab.followupIdsFilter || []).join(','))
      }
      if (!isPageValid) {
        newSearchParams.set('page', '1')
      }
      if (!searchParams.get('orderBy')) {
        newSearchParams.set('orderBy', EPlanningColumns.MED_EVENT)
      }
      if (!searchParams.get('orderDirection')) {
        newSearchParams.set('orderDirection', EPlanningOrderDirection.ASC)
      }
      navigate(`${pathname}?${newSearchParams.toString()}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, pathname])

  useEffect(() => {
    // keep the persisted relativeDaysFromToday in sync with the URL, however it was set
    // (calendar selection, filter update, tab switch, or a manually edited URL)
    if (relativeDaysFromUrl !== null) {
      setStoredPlanningRelativeDay(currentTab.id, currentFilters.relativeDaysFromToday)
    }
  }, [currentTab.id, currentFilters.relativeDaysFromToday, relativeDaysFromUrl])

  useEffect(() => {
    return () => {
      clearStoredPlanningRelativeDay()
    }
  }, [])

  return {
    currentTab,
    currentFilters,
    currentOrder,
    currentPage,
    moveToTab,
    updatePlanningFilters,
    updateOrder,
    updatePageNumber,
    resetFilters,
    getApiParams,
  }
}
